import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Keyboard, Coins, Download, RotateCcw, Settings, LogOut, MessageSquareOff, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  ICON_SIZE,
} from '../design-system';
import { openShortcutsModal, openModal, setSidebarOpen } from '../store';
import { onboardingReplay$ } from '../store/ui$';
import { hideFeedbackTemporarily } from './search/accountCommands';
import SidebarThemeSwitcher from './SidebarThemeSwitcher';

/**
 * Sidebar footer overflow: the account row's `⋯` opens theme, tools, settings
 * and logout. Behavior, item layout and the theme toggle come from the
 * shared DropdownMenu primitives; this file only names the actions.
 */
const SidebarFooterMenu = ({ onOpenUsage, onLogout }) => {
  const { t } = useTranslation(['sidebar']);
  const navigate = useNavigate();

  const goToSettings = () => {
    navigate('/settings');
    setSidebarOpen(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="sidebar-footer-menu-btn"
          aria-label={t('sidebar:showMore')}
        >
          <MoreHorizontal size={ICON_SIZE.sm} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="sidebar-footer-dropdown" side="top" align="end" sideOffset={4}>
        <SidebarThemeSwitcher />

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openShortcutsModal()}>
          <Keyboard size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.shortcuts')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenUsage()}>
          <Coins size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.usage')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => openModal('extension')}>
          <Download size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.extension')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            onboardingReplay$.set(true);
            navigate('/');
            setSidebarOpen(false);
          }}
        >
          <RotateCcw size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.replayTour')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(event) => {
            if (!hideFeedbackTemporarily(t)) event.preventDefault();
          }}
        >
          <MessageSquareOff size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.hideFeedback')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={goToSettings}>
          <Settings size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.settings')}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="sidebar-footer-dropdown__danger"
          data-testid="nav-logout"
          onSelect={() => onLogout()}
        >
          <LogOut size={ICON_SIZE.xxs} />
          <span>{t('sidebar:footer.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SidebarFooterMenu;
